import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"
import Loader from "../../../components/Loader"
import Pagination from "../../../components/Pagination"
import Filter from "../../../components/TableFilters"

const columnHelper = createColumnHelper()

const IllustrationMappingsTable = (props) => {
  const {
    data,
    loading,
    onPaginationChange,
    pagination,
    pageCount,
    sorting,
    setSorting,
    filters,
    setFilters,
    setEditedIllustrationMappingId,
    setDeleteIllustrationMappingId,
  } = props

  const columns = useMemo(
    () => [
      columnHelper.accessor("OriginalValue", {
        header: "Original Value",
        id: "OriginalValue",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("MappedValue", {
        header: "Mapped Value",
        id: "MappedValue",
        size: 400,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("createdBy", {
        header: "Created By",
        id: "createdBy",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("lastModifiedBy", {
        header: "Last Modified By",
        id: "lastModifiedBy",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        meta: {
          filterVariant: "text",
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("createdAt", {
        header: "Created On",
        id: "createdAt",
        size: 200,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: true,
        meta: {
          filterVariant: "text",
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("HerniationMappingId", {
        header: "Actions",
        id: "HerniationMappingId",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              className="bg-secondary hover:bg-secondary_dark px-2 py-1 text-sm text-white rounded"
              onClick={() =>
                setEditedIllustrationMappingId(row.original.HerniationMappingId)
              }
            >
              Edit
            </button>
            <button
              className="bg-red-700 hover:bg-red-800 text-white text-sm py-1 px-2 rounded"
              onClick={() =>
                setDeleteIllustrationMappingId(row.original.HerniationMappingId)
              }
            >
              Delete
            </button>
          </div>
        ),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    pageCount,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
  })

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                    style={{ width: `${column.getSize()}px` }}
                  >
                    <div className="flex flex-col justify-start items-start">
                      <button
                        {...{
                          className: column.column.getCanSort()
                            ? "w-full select-none cursor-pointer flex items-center gap-1"
                            : "w-full",
                          onClick: column.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          column.column.columnDef.header,
                          column.getContext()
                        )}
                        {{
                          asc: <SortAscending className="h-4 w-4" />,
                          desc: <SortDescending className="h-4 w-4" />,
                        }[column.column.getIsSorted()] ?? null}
                      </button>
                      {column.column.getCanFilter() ? (
                        <div className="mt-2">
                          <Filter column={column.column} />
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <div className="py-2">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 border-b border-gray-200"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No Pathology Mappings found</div>
        )}
      </div>
    </>
  )
}

export default IllustrationMappingsTable
